import React, { useMemo, useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import { marked } from 'marked';
import {
  Container,
  Group,
  Stack,
  Title,
  Text,
  Anchor,
  Box,
  ActionIcon,
  Drawer,
  ScrollArea,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Menu, X } from 'lucide-react';
import usageMd from '../docs/usage.md?raw';

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function buildTOC(markdown) {
  const lines = markdown.split('\n');
  const toc = [];
  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)/);
    if (!match) continue;
    toc.push({ level: match[1].length, text: match[2].trim(), id: slugify(match[2].trim()) });
  }
  return toc;
}

function TOCList({ toc, activeId, onClick }) {
  return (
    <Stack gap={0}>
      {toc.map((item) => {
        const isActive = item.id === activeId;
        const indent = (item.level - 1) * 16;
        return (
          <Anchor
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => { e.preventDefault(); onClick(item.id); }}
            c={isActive ? 'blue' : undefined}
            fw={isActive ? 600 : 400}
            fz={item.level <= 2 ? 'sm' : 'xs'}
            lh={1.5}
            pl={8 + indent}
            py={4}
            td="none"
            style={{
              borderLeft: isActive ? '2px solid var(--mantine-color-blue-filled)' : '2px solid transparent',
              marginLeft: -2,
            }}
          >
            {item.text}
          </Anchor>
        );
      })}
    </Stack>
  );
}

export default function Docs() {
  const toc = useMemo(() => buildTOC(usageMd), []);

  const renderer = useMemo(() => {
    const r = new marked.Renderer();
    r.heading = ({ tokens, depth }) => {
      const text = marked.Parser.parseInline(tokens);
      return `<h${depth} id="${slugify(text)}">${text}</h${depth}>`;
    };
    return r;
  }, []);

  const html = useMemo(
    () => marked.parse(usageMd, { gfm: true, breaks: false, renderer }),
    [renderer]
  );

  const contentRef = useRef(null);
  const [activeId, setActiveId] = useState(toc[0]?.id || null);
  const [tocOpen, setTocOpen] = useState(false);
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const computedColorScheme = useComputedColorScheme('light');
  const codeBg = computedColorScheme === 'dark' ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-2)';
  const codeText = computedColorScheme === 'dark' ? 'var(--mantine-color-gray-3)' : 'var(--mantine-color-dark-9)';

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const headingEls = toc.map((h) => document.getElementById(h.id)).filter(Boolean);
    const handleScroll = () => {
      const scrollTop = container.getBoundingClientRect().top * -1 + 80;
      let current = headingEls[0]?.id || null;
      for (const el of headingEls) {
        if (el.offsetTop <= scrollTop + 120) current = el.id;
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc]);

  const scrollToHeading = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    setTocOpen(false);
  }, []);

  // Code-block copy buttons (DOM manipulation for dangerouslySetInnerHTML content)
  useLayoutEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const pres = container.querySelectorAll('pre');
    pres.forEach((pre) => {
      if (pre.parentElement?.dataset?.codeBlockWrapper) return;
      const code = pre.querySelector('code');
      const text = code?.textContent || pre.textContent || '';
      const langClass = code?.className?.match(/language-(\w+)/);
      const lang = langClass ? langClass[1] : null;

      const wrapper = document.createElement('div');
      wrapper.dataset.codeBlockWrapper = 'true';
      wrapper.style.cssText = 'position:relative;margin:1.25em 0;';

      if (lang) {
        const label = document.createElement('span');
        label.textContent = lang;
        label.style.cssText = 'position:absolute;top:10px;left:20px;z-index:1;font-family:monospace;font-size:12px;font-weight:600;color:var(--mantine-color-dimmed);opacity:0.5;text-transform:uppercase;pointer-events:none;';
        wrapper.appendChild(label);
      }

      const btn = document.createElement('button');
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
      const btnBg = computedColorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
      const btnOpacity = computedColorScheme === 'dark' ? '0.7' : '0.5';
      btn.style.cssText = `position:absolute;top:8px;right:8px;z-index:1;display:flex;align-items:center;justify-content:center;width:28px;height:28px;border:none;border-radius:6px;background:${btnBg};color:var(--mantine-color-text);cursor:pointer;opacity:${btnOpacity};transition:all 0.15s ease;`;
      btn.setAttribute('aria-label', 'Copy code');
      btn.title = 'Copy';

      let timeoutId = null;
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          btn.style.opacity = '1';
          btn.style.color = 'var(--mantine-color-green-filled)';
          btn.style.background = 'rgba(16,185,129,0.15)';
          btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
          btn.title = 'Copied!';
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            btn.style.opacity = btnOpacity;
            btn.style.color = 'var(--mantine-color-text)';
            btn.style.background = btnBg;
            btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
            btn.title = 'Copy';
          }, 2000);
        } catch { /* clipboard unavailable */ }
      });
      const hoverBg = computedColorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';
      btn.addEventListener('mouseenter', () => { if (!timeoutId) { btn.style.opacity = '1'; btn.style.background = hoverBg; } });
      btn.addEventListener('mouseleave', () => { if (!timeoutId) { btn.style.opacity = btnOpacity; btn.style.background = btnBg; } });

      wrapper.appendChild(btn);
      pre.style.margin = '0';
      pre.style.padding = '12px 16px 24px 20px';
      pre.style.borderRadius = '8px';
      pre.style.backgroundColor = codeBg;
      pre.style.color = codeText;
      pre.style.position = 'relative';
      pre.parentNode?.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);
    });
  }, [html, codeBg, codeText]);

  const tocContent = (
    <Stack gap={0}>
      <Text fz={12} fw={700} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.08em', opacity: 0.6 }}>
        On this page
      </Text>
      <TOCList toc={toc} activeId={activeId} onClick={scrollToHeading} />
    </Stack>
  );

  return (
    <Container size="xl" py={40}>
      <Group align="flex-start" gap={48} wrap="nowrap">
        {/* Desktop sidebar */}
        <Box visibleFrom="md" w={240} style={{ flexShrink: 0, position: 'sticky', top: 90, maxHeight: 'calc(100vh - 120px)' }}>
          <ScrollArea h="calc(100vh - 140px)" pr="md" style={{ borderRight: '1px solid var(--mantine-color-default-border)' }}>
            {tocContent}
          </ScrollArea>
        </Box>

        {/* Main content */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <div ref={contentRef} dangerouslySetInnerHTML={{ __html: html }} />
        </Box>
      </Group>

      {/* Mobile TOC drawer */}
      <Drawer
        opened={tocOpen}
        onClose={() => setTocOpen(false)}
        title="On this page"
        size="xs"
        hiddenFrom="md"
      >
        <TOCList toc={toc} activeId={activeId} onClick={scrollToHeading} />
      </Drawer>

      {/* Mobile TOC toggle */}
      <ActionIcon
        hiddenFrom="md"
        pos="fixed"
        style={{ bottom: 24, right: 24, zIndex: 60 }}
        size={48}
        radius="xl"
        variant="default"
        onClick={() => setTocOpen(!tocOpen)}
        aria-label="Table of Contents"
      >
        {tocOpen ? <X size={20} /> : <Menu size={20} />}
      </ActionIcon>
    </Container>
  );
}
